"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from 'next/navigation';
import { 
  signInWithPopup, 
  GoogleAuthProvider,
  fetchSignInMethodsForEmail,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut,
  updateProfile
} from "firebase/auth";
import { auth, googleProvider, appleProvider } from '@/firebase/config';
import useAuthStore from '@/store/useAuthStore';
import useOnboardingStore from '@/store/useOnboardingStore';
import {
  trackLoginPageView,
  trackSignupStart,
  trackSignupComplete,
  trackLoginAttempt,
  trackLoginSuccess,
  trackAuthError,
  trackPasswordReset,
  setUserProperties
} from '@/lib/analytics';

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { PasswordInput } from "@/components/ui/password-input";
import { Loader2, AlertCircle, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { validatePassword } from "@/lib/passwordValidation";
import PasswordStrengthIndicator from "@/components/PasswordStrengthIndicator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, setUser } = useAuthStore();
  const { hasCompletedOnboarding, setOnboardingCompleted } = useOnboardingStore();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProvider, setIsLoadingProvider] = useState("");
  const [authMode, setAuthMode] = useState(() => {
    const modeParam = searchParams?.get('mode');
    return modeParam === 'signup' ? 'signup' : 'signin';
  });
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  
  // Form fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState(null);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationDialogMessage, setValidationDialogMessage] = useState("");

  // Track login page view on mount
  useEffect(() => {
    trackLoginPageView();
  }, []);

  // Validate password on change (only in signup mode)
  useEffect(() => {
    if (authMode === "signup" && password) {
      const validation = validatePassword(password);
      setPasswordValidation(validation);
    } else {
      setPasswordValidation(null);
    }
  }, [password, authMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccessMessage("");
    
    // Set flags BEFORE auth to prevent any redirects
    sessionStorage.setItem('isAuthenticating', 'true');
    sessionStorage.setItem('justAuthenticated', 'true');

    try {
      let result;

      if (authMode === "signup") {
        // Track signup start
        trackSignupStart('email');

        // Validate passwords match
        if (password !== confirmPassword) {
          setValidationDialogMessage("Passwords don't match. Please make sure both passwords are identical.");
          setShowValidationDialog(true);
          setIsLoading(false);
          sessionStorage.removeItem('isAuthenticating');
          trackAuthError('passwords_dont_match', 'signup');
          return;
        }

        // Validate password strength and requirements
        const validation = validatePassword(password);
        if (!validation.isValid) {
          // Get the list of failed criteria
          const failedCriteria = Object.entries(validation.criteria)
            .filter(([_, criterion]) => !criterion.passed)
            .map(([_, criterion]) => criterion.message);

          const message = `Your password doesn't meet the security requirements:\n\n${failedCriteria.map(msg => `• ${msg}`).join('\n')}\n\nPlease create a stronger password to continue.`;
          setValidationDialogMessage(message);
          setShowValidationDialog(true);
          setIsLoading(false);
          sessionStorage.removeItem('isAuthenticating');
          trackAuthError('weak_password', 'signup');
          return;
        }

        // Check if account exists
        const methods = await fetchSignInMethodsForEmail(auth, email);
        if (methods.length > 0) {
          setError("account-exists");
          setIsLoading(false);
          sessionStorage.removeItem('isAuthenticating');
          trackAuthError('account_exists', 'signup');
          return;
        }

        // Create account
        result = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(result.user);

        // Track successful signup
        trackSignupComplete('email', result.user.uid);

        await signOut(auth);

        setSuccessMessage("Please check your mail box to verify your account.");
        setAuthMode("signin");
        setPassword("");
        setConfirmPassword("");
        sessionStorage.removeItem('isAuthenticating');
        sessionStorage.removeItem('justAuthenticated');
        setIsLoading(false);
        return;
      } else {
        // Track login attempt
        trackLoginAttempt('email');

        // Sign in
        result = await signInWithEmailAndPassword(auth, email, password);
        await result.user.reload();
        if (result.user && !result.user.emailVerified) {
          const creationTime = new Date(result.user.metadata.creationTime);
          const lastSignInTime = new Date(result.user.metadata.lastSignInTime);
          const isRecentUser = (lastSignInTime - creationTime) < 2 * 60 * 1000; // 2 minutes threshold
          if (isRecentUser) {
            setError("Please verify your email to log in. A verification link has been sent to your inbox.");
            setIsLoading(false);
            sessionStorage.removeItem('isAuthenticating');
            sessionStorage.removeItem('justAuthenticated');
            return;
          }
        }
      }
      
      if (result?.user) {
        // Track successful login
        trackLoginSuccess('email', result.user.uid);

        // Set user properties for GA4
        setUserProperties({
          userId: result.user.uid,
          signupMethod: 'email',
        });

        // Save user to Firestore
        const { saveUserToFirestore } = await import('@/firebase/utils');
        const userData = await saveUserToFirestore(result.user);

        // Handle redirect
        await handleAuthSuccess(result.user, authMode === "signup");
      }
    } catch (error) {
      console.error("Auth error:", error);
      sessionStorage.removeItem('isAuthenticating'); // Clear flag on error
      sessionStorage.removeItem('justAuthenticated'); // Clear flag on error
      handleAuthError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoadingProvider("google");
    setError("");

    // Track login attempt
    trackLoginAttempt('google');

    // Set flags BEFORE popup to prevent any redirects during auth
    sessionStorage.setItem('isAuthenticating', 'true');
    sessionStorage.setItem('justAuthenticated', 'true');

    try {
      const result = await signInWithPopup(auth, googleProvider);

      if (result.user) {
        const { saveUserToFirestore } = await import('@/firebase/utils');
        const userData = await saveUserToFirestore(result.user);

        // Track signup or login success
        if (userData.isNewUser === true) {
          trackSignupComplete('google', result.user.uid);
        } else {
          trackLoginSuccess('google', result.user.uid);
        }

        // Set user properties
        setUserProperties({
          userId: result.user.uid,
          signupMethod: 'google',
        });

        // Pass false for isNewUser if user already exists
        await handleAuthSuccess(result.user, userData.isNewUser === true);
      } else {
        // No user, reset states
        sessionStorage.removeItem('isAuthenticating');
        sessionStorage.removeItem('justAuthenticated');
      }
    } catch (error) {
      console.error("Google sign-in error:", error);
      sessionStorage.removeItem('isAuthenticating'); // Clear flag on error
      sessionStorage.removeItem('justAuthenticated'); // Clear flag on error
      // Ignore popup closed errors
      if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
        handleAuthError(error);
      }
    } finally {
      setIsLoadingProvider("");
    }
  };

  const handleAppleSignIn = async () => {
    setIsLoadingProvider("apple");
    setError("");

    // Track login attempt
    trackLoginAttempt('apple');

    // Set flags BEFORE popup to prevent any redirects during auth
    sessionStorage.setItem('isAuthenticating', 'true');
    sessionStorage.setItem('justAuthenticated', 'true');

    try {
      const result = await signInWithPopup(auth, appleProvider);

      if (result.user) {
        const { saveUserToFirestore } = await import('@/firebase/utils');
        const userData = await saveUserToFirestore(result.user);
        console.log('🟢 [LoginForm] Apple sign-in userData:', userData);

        // Track signup or login success
        if (userData.isNewUser === true) {
          trackSignupComplete('apple', result.user.uid);
        } else {
          trackLoginSuccess('apple', result.user.uid);
        }

        // Set user properties
        setUserProperties({
          userId: result.user.uid,
          signupMethod: 'apple',
        });

        // Pass false for isNewUser if user already exists
        await handleAuthSuccess(result.user, userData.isNewUser === true);
      } else {
        // No user, reset states
        sessionStorage.removeItem('isAuthenticating');
        sessionStorage.removeItem('justAuthenticated');
      }
    } catch (error) {
      console.error("Apple sign-in error:", error);
      sessionStorage.removeItem('isAuthenticating'); // Clear flag on error
      sessionStorage.removeItem('justAuthenticated'); // Clear flag on error
      // Ignore popup closed errors
      if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
        handleAuthError(error);
      }
    } finally {
      setIsLoadingProvider("");
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError("Please enter your email address first");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await sendPasswordResetEmail(auth, email);
      trackPasswordReset('request');
      setSuccessMessage("Password reset email sent! Check your inbox and spam folder.");
    } catch (error) {
      handleAuthError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthSuccess = async (user, isNewUser) => {
    
    // Note: isAuthenticating flag is already set before auth operation
    
    // Check if user has already completed onboarding
    if (user?.uid) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Only skip onboarding if onboardingCompleted is explicitly true
          if (userData.onboardingCompleted === true) {
            
            // Check and sync Pro status from RevenueCat for returning users
            try {
              const revenueCatService = (await import('@/services/revenueCatService')).default;
              const hasProFromRevenueCat = await revenueCatService.checkAndSyncProStatus(user.uid);
            } catch (error) {
              console.error('Error syncing Pro status during login:', error);
              // Continue anyway - don't block login
            }
            
            sessionStorage.removeItem('showOnboarding'); // Clear flag
            router.replace('/agenda'); // Use replace instead of push
            // Clear auth flag after navigation completes
            setTimeout(() => {
              sessionStorage.removeItem('isAuthenticating');
            }, 1000);
            return;
          }
          
          // If onboardingCompleted is false, undefined, or doesn't exist, show onboarding
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      }
    }
    
    // Show onboarding if:
    // - User is new, OR
    // - onboardingCompleted is not true (false, undefined, or field doesn't exist)
    sessionStorage.setItem('showOnboarding', 'true'); // Set flag for AuthProvider
    setOnboardingCompleted(false);
    router.replace('/onboarding'); // Use replace instead of push
    // Clear auth flag after navigation completes
    setTimeout(() => {
      sessionStorage.removeItem('isAuthenticating');
    }, 1000);
  };
  

  const handleAuthError = (error) => {
    // Track the error
    const errorType = error.code?.replace('auth/', '') || 'unknown';
    const context = authMode === 'signup' ? 'signup' : 'login';
    trackAuthError(errorType, context);

    // Use dialog for critical errors, inline messages for minor ones
    switch (error.code) {
      case 'auth/user-not-found':
        setError('No account found with this email.');
        break;
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        setError('Incorrect password. Please try again.');
        break;
      case 'auth/email-already-in-use':
        setValidationDialogMessage('An account already exists with this email address. Please sign in instead or use a different email.');
        setShowValidationDialog(true);
        break;
      case 'auth/weak-password':
        setValidationDialogMessage('Your password is too weak. Please create a stronger password that meets all security requirements:\n\n• At least 8 characters\n• One uppercase letter (A-Z)\n• One lowercase letter (a-z)\n• One number (0-9)\n• One special character (!@#$...)');
        setShowValidationDialog(true);
        break;
      case 'auth/invalid-email':
        setValidationDialogMessage('The email address you entered is not valid. Please check and try again.');
        setShowValidationDialog(true);
        break;
      case 'auth/popup-closed-by-user':
        setError('Sign in cancelled.');
        break;
      case 'auth/network-request-failed':
        setValidationDialogMessage('Network error. Please check your internet connection and try again.');
        setShowValidationDialog(true);
        break;
      case 'auth/too-many-requests':
        setValidationDialogMessage('Too many failed attempts. Please wait a few minutes before trying again.');
        setShowValidationDialog(true);
        break;
      default:
        setValidationDialogMessage('An unexpected error occurred. Please try again or contact support if the problem persists.');
        setShowValidationDialog(true);
    }
  };

  return (
    <Card className="border-0 shadow-none">
      <CardHeader className="space-y-1 pb-8">
        <CardTitle className="text-3xl font-semibold tracking-tight text-center">
          {authMode === "signup" ? "Try AVA for free!" : "Welcome back"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-600">
              {error === "account-exists" ? (
                <span>
                  An account already exists with this email.{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setAuthMode("signin");
                      setError("");
                    }}
                    className="underline hover:no-underline font-semibold text-red-700"
                  >
                    Sign in instead
                  </button>
                </span>
              ) : (
                error
              )}
            </div>
          </div>
        )}
        
        {successMessage && (
          <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-800">{successMessage}</div>
          </div>
        )}

        {/* Social Login Buttons First */}
        <div className="space-y-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleSignIn}
            disabled={isLoadingProvider === "google"}
            className="h-10 w-full"
          >
            {isLoadingProvider === "google" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            )}
            {authMode === "signup" ? "Sign up with Google" : "Log in with Google"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleAppleSignIn}
            disabled={isLoadingProvider === "apple"}
            className="h-10 w-full"
          >
            {isLoadingProvider === "apple" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M17.05 20.28c-.98.95-2.05.88-3.08.35-1.09-.55-2.08-.54-3.24 0-1.44.68-2.2.53-3.06-.35C2.79 15.15 3.51 7.46 9.05 7.12c1.35.07 2.29.74 3.08.74.77 0 2.21-.87 3.85-.72 1.33.13 2.53.71 3.27 1.8-3.02 1.81-2.59 5.83.51 6.97-.59 1.52-1.35 3.02-2.71 4.37zM12.03 7.08c-.14-2.28 1.78-4.14 3.93-4.29.29 2.59-2.37 4.53-3.93 4.29z"
                />
              </svg>
            )}
            {authMode === "signup" ? "Sign up with Apple" : "Log in with Apple"}
          </Button>
        </div>

        <div className="relative py-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-gray-400" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-white px-3 text-gray-500">Or continue with email</span>
          </div>
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs font-normal text-gray-600">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
              autoComplete="email"
              className="h-10 border-gray-300 focus:border-gray-400"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-xs font-normal text-gray-600">
                Password
              </Label>
              {authMode === "signin" && (
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                  disabled={isLoading}
                >
                  Forgot password?
                </button>
              )}
            </div>
            <PasswordInput
              id="password"
              placeholder={authMode === "signup" ? "Create a password" : "Enter your password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              showPassword={showPassword}
              onToggleVisibility={() => setShowPassword(!showPassword)}
              disabled={isLoading}
              required
              autoComplete={authMode === "signup" ? "new-password" : "current-password"}
            />
            {authMode === "signup" && passwordValidation && (
              <PasswordStrengthIndicator
                password={password}
                validation={passwordValidation}
                showCriteria={true}
              />
            )}
          </div>

          {authMode === "signup" && (
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-xs font-normal text-gray-600">
                Confirm Password
              </Label>
              <PasswordInput
                id="confirmPassword"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                showPassword={showConfirmPassword}
                onToggleVisibility={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isLoading}
                required
                autoComplete="new-password"
                ariaLabel={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
              />
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white"
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {authMode === "signup" ? "Get started free" : "Sign in with email"}
          </Button>
        </form>

        <div className="text-center text-sm">
          <span className="text-muted-foreground">
            {authMode === "signin" ? "Don't have an account? " : "Already have an account? "}
          </span>
          <button
            type="button"
            onClick={() => {
              setAuthMode(authMode === "signin" ? "signup" : "signin");
              setError("");
              setSuccessMessage("");
              setPassword("");
              setConfirmPassword("");
              setShowPassword(false);
              setShowConfirmPassword(false);
            }}
            className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
          >
            {authMode === "signin" ? "Sign up" : "Sign in"}
          </button>
        </div>

        {/* Password Validation Dialog */}
        <AlertDialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Password Requirements Not Met</AlertDialogTitle>
              <AlertDialogDescription className="whitespace-pre-line text-left">
                {validationDialogMessage}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setShowValidationDialog(false)}>
                Got it
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}